import React, { useEffect, useMemo, useRef, useState } from "react";
import { fallbackArticles, fallbackContact, fallbackOffice, fallbackProfile, fallbackResources, fallbackServices, trustItems } from "../lib/content";
import { loadPublicContent, submitEnquiryWithDocuments } from "../lib/data";
import { articleHref } from "../lib/articles";
import { LanguageSwitcher, localizedField, useI18n } from "../lib/i18n";
import { validateDocumentFiles } from "../../shared/uploadValidation";
import { Brand, Icon } from "./Icons";
import InstallAppButton from "./InstallAppButton";

const blankForm = {
  name: "",
  surname: "",
  email: "",
  phone: "",
  service: "",
  preferredDate: "",
  message: "",
  company: "",
};

function cleanPhone(value) {
  return String(value || "").replace(/[^+\d]/g, "");
}

function whatsappUrl(contact, profile) {
  if (contact?.whatsapp_link) return contact.whatsapp_link;
  const number = cleanPhone(profile?.whatsapp || profile?.phone || fallbackProfile.whatsapp).replace("+", "");
  return `https://wa.me/${number}`;
}

function SectionHeading({ eyebrow, title, description }) {
  return (
    <div className="section-heading">
      <span>{eyebrow}</span>
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
    </div>
  );
}

function ContactItem({ icon, href, children }) {
  return (
    <a className="contact-item" href={href}>
      <Icon name={icon} size={18} />
      <span>{children}</span>
    </a>
  );
}

export default function PublicSite() {
  const { language, t } = useI18n();
  const [content, setContent] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [documents, setDocuments] = useState([]);
  const [formState, setFormState] = useState({ status: "idle", message: "" });
  const fileInputRef = useRef(null);
  const formOpenedAtRef = useRef(performance.now());

  useEffect(() => {
    let active = true;
    loadPublicContent()
      .then((result) => active && setContent(result))
      .catch((error) => active && setContent({
        connected: false,
        connectionWarning: error.message,
        profile: fallbackProfile,
        office: fallbackOffice,
        contact: fallbackContact,
        branding: null,
        services: fallbackServices,
        resources: fallbackResources,
        articles: fallbackArticles,
      }));
    return () => { active = false; };
  }, []);

  const data = content || {
    profile: fallbackProfile,
    office: fallbackOffice,
    contact: fallbackContact,
    branding: null,
    services: fallbackServices,
    resources: fallbackResources,
    articles: fallbackArticles,
  };

  const profile = data.profile || fallbackProfile;
  const office = data.office || fallbackOffice;
  const contact = data.contact || fallbackContact;
  const logoUrl = data.branding?.logo_url || "";
  const heroImage = data.branding?.hero_image || "/hasime-hero.png";
  const callHref = `tel:${cleanPhone(contact.phone || profile.phone)}`;
  const emailHref = `mailto:${contact.email || profile.email}`;
  const address = [office.address, office.city].filter(Boolean).join(", ");
  const services = data.services || fallbackServices;
  const resourceItems = data.resources?.length ? data.resources : fallbackResources;
  const articleItems = data.articles?.length ? data.articles : fallbackArticles;
  const minDate = useMemo(() => new Date().toISOString().slice(0, 16), []);

  const closeMenu = () => setMobileOpen(false);

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const updateDocuments = (event) => {
    try {
      const selected = Array.from(event.target.files || []);
      validateDocumentFiles(selected);
      setDocuments(selected);
      setFormState({ status: "idle", message: "" });
    } catch (error) {
      setDocuments([]);
      event.target.value = "";
      setFormState({ status: "error", message: t(error.message) });
    }
  };

  const removeDocument = (index) => {
    setDocuments((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (form.company) return;
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      setFormState({ status: "error", message: t("Plotësoni emrin, telefonin dhe mesazhin.") });
      return;
    }

    const hasDocuments = documents.length > 0;
    setFormState({ status: "sending", message: hasDocuments ? t("Duke ruajtur kërkesën dhe dokumentet…") : t("Duke dërguar kërkesën…") });
    try {
      const preparedForm = { ...form, name: `${form.name.trim()} ${form.surname.trim()}`.trim(), preferredLanguage: language };
      const formElapsedMs = Math.max(0, Math.round(performance.now() - formOpenedAtRef.current));
      const result = await submitEnquiryWithDocuments(preparedForm, documents, formElapsedMs);
      setForm(blankForm);
      setDocuments([]);
      formOpenedAtRef.current = performance.now();
      if (fileInputRef.current) fileInputRef.current.value = "";
      const reference = result.caseReference ? ` ${t("Referenca juaj është {{reference}}.", { reference: result.caseReference })}` : "";
      setFormState({ status: "success", message: `${hasDocuments ? t("Kërkesa dhe dokumentet u ruajtën në mënyrë private.") : t("Kërkesa u regjistrua me sukses.")}${reference} ${t("Do t’ju kontaktojmë sa më shpejt.")}` });
    } catch (error) {
      setFormState({ status: "error", message: t("Kërkesa nuk u dërgua: {{error}}", { error: error.message }) });
    }
  };

  return (
    <div className="site-shell">
      {content?.connectionWarning ? (
        <div className="connection-banner" role="status">
          <Icon name="warning" size={16} />
          <span>{t(content.connectionWarning)}</span>
        </div>
      ) : null}

      <header className="site-header">
        <a href="#kreu" className="brand-link" aria-label={t("Kreu")}><Brand logoUrl={logoUrl} /></a>
        <nav className={`main-nav ${mobileOpen ? "main-nav--open" : ""}`} aria-label={t("Navigimi kryesor")}>
          <a href="#kreu" onClick={closeMenu}>{t("Kreu")}</a>
          <a href="#rreth" onClick={closeMenu}>{t("Rreth Nesh")}</a>
          <a href="#sherbimet" onClick={closeMenu}>{t("Shërbimet")}</a>
          <a href="#burimet" onClick={closeMenu}>{t("Burime Ligjore")}</a>
          <a href="#artikujt" onClick={closeMenu}>{t("Këshilla Ligjore")}</a>
          <a href="#kontakt" onClick={closeMenu}>{t("Kontakt")}</a>
        </nav>
        <div className="header-actions">
          <LanguageSwitcher compact />
          <InstallAppButton />
          <a className="admin-link" href="/admin"><Icon name="user" size={17} /> Admin</a>
        </div>
        <button className="menu-button" type="button" onClick={() => setMobileOpen((open) => !open)} aria-expanded={mobileOpen} aria-label={t("Hap menynë")}>
          <Icon name={mobileOpen ? "close" : "menu"} size={25} />
        </button>
      </header>

      <main>
        <section id="kreu" className="hero" style={{ "--hero-image": `url(${heroImage})` }}>
          <div className="hero__content">
            <p className="eyebrow">{t("Avokate në Prishtinë, Kosovë")}</p>
            <h1>{t("Zgjidhje Ligjore.")}<br /><span>{t("Mbrojtje e Sigurt.")}</span></h1>
            <p className="hero__lead">{t("Mbrojtje juridike profesionale, konfidencialitet dhe përfaqësim me integritet.")}</p>
            <div className="hero__actions">
              <a className="button button--gold" href="#kontakt"><Icon name="calendar" size={17} /> {t("Rezervo Konsultë")}</a>
              <a className="button button--outline" href={callHref}><Icon name="phone" size={17} /> {t("Na Kontaktoni")}</a>
            </div>
            <div className="hero__contact">
              <ContactItem icon="phone" href={callHref}>{contact.phone || profile.phone}</ContactItem>
              <ContactItem icon="mail" href={emailHref}>{contact.email || profile.email}</ContactItem>
              <ContactItem icon="pin" href="#kontakt">{address}</ContactItem>
            </div>
          </div>
        </section>

        <section className="trust-grid" aria-label={t("Vlerat tona")}>
          {trustItems.map((item) => (
            <article className="trust-card" key={item.title}>
              <span className="icon-badge"><Icon name={item.icon} size={27} /></span>
              <div><h3>{localizedField(item, "title", language)}</h3><p>{localizedField(item, "text", language)}</p><a href="#rreth">{t("Më shumë")} <Icon name="arrow" size={14} /></a></div>
            </article>
          ))}
        </section>

        <section className="language-service-banner" aria-label={t("Shërbime edhe në gjuhën serbe")}>
          <span className="icon-badge"><Icon name="message" size={24} /></span>
          <div><strong>{t("Shërbime edhe në gjuhën serbe")}</strong><p>{t("Këshillim, përkthim dokumentesh, komunikim dhe përfaqësim juridik në shqip, anglisht dhe serbisht.")}</p></div>
          <a className="button button--outline button--small" href="#kontakt">{t("Na Kontaktoni")}</a>
        </section>

        <div className="content-grid content-grid--three">
          <section id="sherbimet" className="panel services-panel">
            <SectionHeading eyebrow={t("Shërbimet tona")} title={t("Mbrojtje për çdo çështje")} />
            <div className="service-grid">
              {services.map((service, index) => (
                <article className="service-card" key={service.id || service.title}>
                  <Icon name={service.icon || fallbackServices[index % fallbackServices.length].icon} size={25} />
                  <h3>{localizedField(service, "title", language)}</h3>
                  <p>{localizedField(service, "description", language)}</p>
                </article>
              ))}
            </div>
            <a className="panel-link" href="#kontakt">{t("Kërko këshillim për shërbimin")} <Icon name="arrow" size={15} /></a>
          </section>

          <section id="rreth" className="panel about-panel">
            <SectionHeading eyebrow={t("Rreth Hasime Osmani-Vraniqi")} title={t("Përfaqësim i qartë dhe i besueshëm")} />
            <div className="about-layout">
              <img src={profile.photo_url || "/hasime-hero.png"} onError={(event) => { event.currentTarget.src = "/hasime-hero.png"; }} alt="Hasime Osmani-Vraniqi" />
              <div>
                <p>{localizedField(profile, "bio", language)}</p>
                <ul>
                  <li><Icon name="check" size={15} /> {t("Qasje personale ndaj çdo çështjeje")}</li>
                  <li><Icon name="check" size={15} /> {t("Komunikim i qartë dhe i vazhdueshëm")}</li>
                  <li><Icon name="check" size={15} /> {t("Konfidencialitet dhe integritet i plotë")}</li>
                  <li><Icon name="check" size={15} /> {t("Përkushtim maksimal për rezultatet tuaja")}</li>
                </ul>
                <div className="about-actions">
                  <a className="button button--gold button--small" href="#kontakt">{t("Rezervo Konsultë")}</a>
                  <a className="button button--outline button--small" href={callHref}>{t("Na Kontaktoni")}</a>
                </div>
              </div>
            </div>
          </section>

          <section id="burimet" className="panel resources-panel">
            <SectionHeading eyebrow={t("Burime Ligjore — Kosovë")} title={t("Lidhje zyrtare dhe të dobishme")} />
            <div className="resource-grid">
              {resourceItems.map((resource, index) => (
                <a className="resource-card" href={/^https?:\/\//i.test(resource.url || "") ? resource.url : "#kontakt"} target="_blank" rel="noreferrer" key={resource.id || resource.title}>
                  <Icon name={resource.icon || fallbackServices[index % fallbackServices.length].icon || "book"} size={25} />
                  <strong>{localizedField(resource, "title", language)}</strong>
                  <small>{localizedField(resource, "description", language) || t("Hap faqen zyrtare")}</small>
                  <Icon name="external" size={13} className="resource-card__external" />
                </a>
              ))}
            </div>
            <a className="panel-link" href="#kontakt">{t("Keni nevojë për interpretim ligjor?")} <Icon name="arrow" size={15} /></a>
          </section>
        </div>

        <div className="content-grid content-grid--bottom">
          <section id="kontakt" className="panel contact-panel">
            <SectionHeading eyebrow={t("Konsultë / Kontakt")} title={t("Na tregoni shkurt çështjen tuaj")} />
            <div className="contact-layout">
              <aside className="contact-card-list">
                <ContactItem icon="phone" href={callHref}>{contact.phone || profile.phone}</ContactItem>
                <ContactItem icon="mail" href={emailHref}>{contact.email || profile.email}</ContactItem>
                <ContactItem icon="pin" href={office.google_maps || "#kontakt"}>{address}</ContactItem>
                <a className="button button--whatsapp" href={whatsappUrl(contact, profile)} target="_blank" rel="noreferrer">WhatsApp</a>
              </aside>
              <form className="contact-form" onSubmit={handleSubmit}>
                <label className="honeypot" aria-hidden="true">{t("Company")}<input name="company" value={form.company} onChange={updateForm} tabIndex="-1" autoComplete="off" /></label>
                <div className="form-row">
                  <label>{t("Emri")}<input name="name" value={form.name} onChange={updateForm} required autoComplete="given-name" /></label>
                  <label>{t("Mbiemri")}<input name="surname" value={form.surname} onChange={updateForm} autoComplete="family-name" /></label>
                </div>
                <div className="form-row">
                  <label>Email<input type="email" name="email" value={form.email} onChange={updateForm} autoComplete="email" /></label>
                  <label>{t("Telefon")}<input name="phone" value={form.phone} onChange={updateForm} required autoComplete="tel" /></label>
                </div>
                <label>{t("Lloji i çështjes")}
                  <select name="service" value={form.service} onChange={updateForm}>
                    <option value="">{t("Zgjidhni një shërbim")}</option>
                    {services.map((service) => <option key={service.id || service.title} value={service.title}>{localizedField(service, "title", language)}</option>)}
                  </select>
                </label>
                <label>{t("Orari i preferuar (opsional)")}<input type="datetime-local" min={minDate} name="preferredDate" value={form.preferredDate} onChange={updateForm} /></label>
                <label>{t("Mesazhi juaj")}<textarea name="message" value={form.message} onChange={updateForm} required rows="4" /></label>
                <div className="document-upload">
                  <div className="document-upload__heading">
                    <span className="icon-badge"><Icon name="document" size={20} /></span>
                    <div><strong>{t("Dokumentet e rastit (opsionale)")}</strong><small>{t("PDF, DOCX, JPG, PNG, WEBP ose HEIC. Deri në 5 skedarë, 10 MB secili.")}</small></div>
                  </div>
                  <label className="document-upload__picker" htmlFor="customer-documents">
                    <Icon name="upload" size={19} />
                    <span>{t("Zgjidhni dokumentet")}</span>
                    <input ref={fileInputRef} id="customer-documents" type="file" multiple accept=".pdf,.docx,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={updateDocuments} />
                  </label>
                  {documents.length ? (
                    <ul className="document-upload__list">
                      {documents.map((document, index) => (
                        <li key={`${document.name}-${document.lastModified}-${index}`}>
                          <span><Icon name="document" size={14} /> <strong>{document.name}</strong><small>{(document.size / 1024 / 1024).toFixed(2)} MB</small></span>
                          <button type="button" onClick={() => removeDocument(index)} aria-label={`${t("Hiq")} ${document.name}`}><Icon name="close" size={14} /></button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <small className="document-upload__security"><Icon name="shield" size={14} /> {t("Mbrojtja ndaj abuzimit zbatohet automatikisht. Nuk kërkohet CAPTCHA.")}</small>
                </div>
                <button className="button button--gold button--wide" type="submit" disabled={formState.status === "sending"}>
                  {formState.status === "sending" ? t("Duke dërguar…") : documents.length ? t("Dërgo Kërkesën dhe Dokumentet") : t("Dërgo Kërkesën")}
                </button>
                {formState.message ? <p className={`form-message form-message--${formState.status}`} role="status">{formState.message}</p> : null}
                <small className="privacy-note">{t("Dokumentet ruhen në një hapësirë private dhe hapen nga administratori vetëm përmes lidhjeve me afat të shkurtër. Mos ngarkoni fjalëkalime ose çelësa identifikimi.")}</small>
              </form>
            </div>
          </section>

          <section id="artikujt" className="panel articles-panel">
            <SectionHeading eyebrow={t("Këshilla Ligjore — Artikuj")} title={t("Informacion praktik për vendime më të sigurta")} />
            <div className="article-grid">
              {articleItems.map((article, index) => (
                <a className="article-card" href={articleHref(article)} aria-label={`${t("Lexo artikullin")}: ${localizedField(article, "title", language)}`} key={article.id || article.title}>
                  <div className="article-card__visual" style={article.image_url ? { backgroundImage: `url(${article.image_url})` } : undefined}>
                    {!article.image_url ? <Icon name={article.icon || ["gavel", "document", "home"][index % 3]} size={45} /> : null}
                  </div>
                  <div className="article-card__body">
                    <h3>{localizedField(article, "title", language)}</h3>
                    <p>{String(localizedField(article, "content", language)).slice(0, 145)}{String(localizedField(article, "content", language)).length > 145 ? "…" : ""}</p>
                    <span className="article-read-more">{t("Lexo më shumë")} <Icon name="arrow" size={14} /></span>
                  </div>
                </a>
              ))}
            </div>
            <a className="panel-link panel-link--center" href="#kontakt">{t("Kërko këshillë për rastin tuaj")} <Icon name="arrow" size={15} /></a>
          </section>
        </div>
      </main>

      <footer className="site-footer">
        <Brand logoUrl={logoUrl} compact />
        <p>© {new Date().getFullYear()} Juris Prudent Studio Ligjore. {t("Të gjitha të drejtat e rezervuara.")}</p>
        <a href="/admin">{t("Hyrja e administratës")}</a>
      </footer>

      <nav className="mobile-bottom-nav" aria-label={t("Navigimi mobil")}>
        <a href="#kreu"><Icon name="home" size={20} /><span>{t("Kreu")}</span></a>
        <a href="#sherbimet"><Icon name="grid" size={20} /><span>{t("Shërbimet")}</span></a>
        <a href="#burimet"><Icon name="book" size={20} /><span>{t("Burime")}</span></a>
        <a href={callHref}><Icon name="phone" size={20} /><span>{t("Kontakt")}</span></a>
      </nav>
    </div>
  );
}
